import { useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useNavigationActions,
  useCurrentNavigation,
  NavigationEntry,
} from "@/stores/navigation";
import {
  useViewerProject,
  useViewerPages,
  useViewerCurrentPageId,
} from "@/stores/viewer/viewer-store";

// ============================================================================
// Types
// ============================================================================

/**
 * Type of breadcrumb entry for display purposes.
 * - 'app': Application-level navigation (Dashboard, Settings)
 * - 'project-home': The home/default page of the current project
 * - 'page': A regular page in the navigation history
 */
export type BreadcrumbType = 'app' | 'project-home' | 'page';

/**
 * A breadcrumb item for display in the UI.
 */
export interface ProjectBreadcrumbItem {
  /** Unique identifier */
  id: string;
  /** Display label */
  label: string;
  /** Route href (undefined for current page) */
  href?: string;
  /** Whether this is the current page */
  isCurrentPage?: boolean;
  /** Optional icon name */
  icon?: string;
  /** Type of breadcrumb for styling */
  type: BreadcrumbType;
  /** Optional index in history (for direct navigation) */
  historyIndex?: number;
  /** Page ID (for project pages) */
  pageId?: string;
}

/**
 * Action available on a breadcrumb item.
 */
export interface BreadcrumbAction {
  id: string;
  label: string;
  icon?: string;
  action: () => void;
  shortcut?: string[];
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Dynamic breadcrumb generation for the viewer.
 *
 * Builds breadcrumbs from:
 * 1. Dashboard (app exit point)
 * 2. Home anchor (project's default page, always visible)
 * 3. Current page (if different from home)
 *
 * Example: [Dashboard] > [Home] > [Current Page]
 */
export function useBreadcrumbs() {
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string; pageId?: string }>();

  // Store hooks
  const project = useViewerProject();
  const pages = useViewerPages();
  const currentPageId = useViewerCurrentPageId();
  const currentNavigation = useCurrentNavigation();
  const { navigateToHistoryIndex } = useNavigationActions();

  // Find the home/default page
  const homePage = useMemo(() => {
    if (!pages.length) return null;
    return pages.find((p) => p.isDefault) || pages[0];
  }, [pages]);

  // Build breadcrumbs
  const breadcrumbs = useMemo((): ProjectBreadcrumbItem[] => {
    const crumbs: ProjectBreadcrumbItem[] = [];

    // 1. Dashboard - always first
    crumbs.push({
      id: "dashboard",
      label: "Dashboard",
      href: "/dashboard",
      icon: "LayoutDashboard",
      type: "app",
      isCurrentPage: false,
    });

    // 2. Home anchor - always visible (if we have a project)
    if (project && projectId && homePage) {
      const isHomeCurrentPage = currentPageId === homePage.id;
      crumbs.push({
        id: "project-home",
        label: homePage.name || "Home",
        href: isHomeCurrentPage ? undefined : `/viewer/${projectId}/page/${homePage.id}`,
        icon: "Home",
        type: "project-home",
        isCurrentPage: isHomeCurrentPage,
        pageId: homePage.id,
      });

      // If home is current page, we're done
      if (isHomeCurrentPage) {
        return crumbs;
      }

      // 3. Current page (not home) — flat sibling structure, no history trail
      if (currentPageId && currentPageId !== homePage.id) {
        const currentPage = pages.find((p) => p.id === currentPageId);
        crumbs.push({
          id: `page-${currentPageId}`,
          label: currentPage?.name || currentNavigation?.pageName || `Page ${currentPageId.slice(0, 8)}`,
          type: "page",
          isCurrentPage: true,
          pageId: currentPageId,
        });
      }
    }

    return crumbs;
  }, [
    project,
    projectId,
    homePage,
    pages,
    currentPageId,
    currentNavigation,
  ]);

  /**
   * Handle breadcrumb click navigation.
   * Uses history index for direct navigation if available,
   * otherwise falls back to URL navigation.
   */
  const handleBreadcrumbClick = useCallback(
    (item: ProjectBreadcrumbItem) => {
      if (item.isCurrentPage) {
        return; // Don't navigate to current page
      }

      // For history entries with an index, use direct navigation
      if (typeof item.historyIndex === "number" && item.historyIndex >= 0) {
        const entry = navigateToHistoryIndex(item.historyIndex);
        if (entry) {
          navigate(`/viewer/${entry.projectId}/page/${entry.pageId}`, { replace: false });
          return;
        }
      }

      // Fall back to href navigation
      if (item.href) {
        navigate(item.href);
      }
    },
    [navigate, navigateToHistoryIndex]
  );

  /**
   * Get the parent breadcrumb for keyboard navigation (Alt+Up)
   */
  const getParentBreadcrumb = useCallback((): ProjectBreadcrumbItem | null => {
    const currentCrumbIndex = breadcrumbs.findIndex((crumb) => crumb.isCurrentPage);
    if (currentCrumbIndex > 0) {
      return breadcrumbs[currentCrumbIndex - 1];
    }
    return null;
  }, [breadcrumbs]);

  return {
    breadcrumbs,
    handleBreadcrumbClick,
    getParentBreadcrumb,
    homePage,
    currentPageId,
    projectId,
  };
}

// Re-export types for component usage
export type { NavigationEntry };
